import React, { useContext, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ActivityList from './ActivityList';
import './progress.css';
import { BottomNav } from '../../common/BottomNav';
import { FeedbackLink } from '../../common/FeedbackLink';
import { DataContext } from '../../../context/DataContext';
import { deleteActivity, deleteActivityStorageFiles, deletePublicSharesForActivity } from '../../../firebase/services';

const extractLevelName = (levelData) => {
  if (!levelData) return null;

  let levelStr = null;

  if (typeof levelData === 'string') {
    levelStr = levelData.toLowerCase();
  } else if (levelData.title) {
    levelStr = levelData.title.toLowerCase();
  } else if (levelData.id && typeof levelData.id === 'string') {
    levelStr = levelData.id.toLowerCase();
  }

  if (!levelStr) return null;

  if (levelStr.includes('streetlight') || levelStr.includes('level 1')) return 'streetlight';
  if (levelStr.includes('daylight') || levelStr.includes('level 2')) return 'daylight';
  if (levelStr.includes('sunshield') || levelStr.includes('level 3')) return 'sunshield';

  return null;
};

const ProgressScreen = () => {
  const { t } = useTranslation();
  const { activities, loading, error, loadMoreActivities, hasMore, removeActivityFromState } = useContext(DataContext);
  const navigate = useNavigate();
  const [selectedCauses, setSelectedCauses] = useState([]);
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [inputValue, setInputValue] = useState('');
  const searchTimerRef = useRef(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    searchTimerRef.current = setTimeout(() => {
      setSearchQuery(value);
    }, 300);
  };

  const clearSearch = () => {
    setInputValue('');
    setSearchQuery('');
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
  };

  const toggleFilter = (filterArray, setFilterArray, value) => {
    if (filterArray.includes(value)) {
      setFilterArray(filterArray.filter(v => v !== value));
    } else {
      setFilterArray([...filterArray, value]);
    }
  };

  const clearFilters = () => {
    setSelectedCauses([]);
    setSelectedLevels([]);
  };

  const filteredActivities = useMemo(() => {
    let result = activities;

    // Apply search filter first
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(activity => {
        const title = (activity.title || '').toLowerCase();
        const reflection = (activity.reflection || '').toLowerCase();
        return title.includes(query) || reflection.includes(query);
      });
    }

    // Then apply cause filter
    if (selectedCauses.length > 0) {
      result = result.filter(activity => {
        const causeTitle = activity.cause?.title || '';
        return selectedCauses.some(selected => causeTitle.includes(selected));
      });
    }

    // Then apply level filter
    if (selectedLevels.length > 0) {
      result = result.filter(activity => {
        const levelName = extractLevelName(activity.cause?.level);
        return levelName && selectedLevels.includes(levelName);
      });
    }

    return result;
  }, [activities, searchQuery, selectedCauses, selectedLevels]);

  const filtersActive = selectedCauses.length > 0 || selectedLevels.length > 0;
  const searchActive = searchQuery.trim().length > 0;

  const handleEdit = (activity) => {
    navigate(`/edit-activity/${activity.id}`, { state: { activity } });
  };

  const handleDeleteRequest = (activity) => {
    setShowDeleteConfirm(activity);
  };

  const handleDeleteConfirm = async () => {
    const activity = showDeleteConfirm;
    setShowDeleteConfirm(null);

    // Remove from UI immediately
    removeActivityFromState(activity.id);

    // Permanently delete from Firestore
    try {
      await deleteActivity(activity.id);
      // Take down any public share pages for this activity
      await deletePublicSharesForActivity(activity.userId, activity.id);
      // Clean up Storage files
      if (activity.imageUrl || activity.thumbnailUrl) {
        await deleteActivityStorageFiles(
          activity.userId,
          activity.id,
          activity.imageUrl,
          activity.thumbnailUrl
        );
      }
    } catch (error) {
      console.error('Failed to delete activity:', error);
    }
  };

  return (
    <div className="progress-screen">
      <header className="progress-header">
        <button className="back-button" onClick={() => window.history.back()}>←</button>
        <h1>{t('progress.title')}</h1>
      </header>

      <div className="search-bar">
        <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="#808080" strokeWidth="2">
          <circle cx="10" cy="10" r="7" />
          <line x1="15" y1="15" x2="21" y2="21" />
        </svg>
        <input
          type="text"
          className="search-input"
          placeholder={t('progress.search')}
          value={inputValue}
          onChange={handleSearchChange}
        />
        {inputValue && (
          <button className="search-clear" onClick={clearSearch}>
            ✕
          </button>
        )}
      </div>

      <div className="filter-section">
        <div className="filter-title">{t('progress.filterByCause')}</div>
        <div className="filter-chips">
          <button
            className={`filter-chip ${selectedCauses.includes('Homelessness') ? 'active' : ''}`}
            onClick={() => toggleFilter(selectedCauses, setSelectedCauses, 'Homelessness')}
          >
            Homelessness
          </button>
          <button
            className={`filter-chip ${selectedCauses.includes('Beautification') ? 'active' : ''}`}
            onClick={() => toggleFilter(selectedCauses, setSelectedCauses, 'Beautification')}
          >
            Beautification
          </button>
          <button
            className={`filter-chip ${selectedCauses.includes('Trafficking') ? 'active' : ''}`}
            onClick={() => toggleFilter(selectedCauses, setSelectedCauses, 'Trafficking')}
          >
            Trafficking
          </button>
        </div>

        <div className="filter-title">{t('progress.filterByLevel')}</div>
        <div className="filter-chips">
          <button
            className={`filter-chip ${selectedLevels.includes('streetlight') ? 'active' : ''}`}
            onClick={() => toggleFilter(selectedLevels, setSelectedLevels, 'streetlight')}
          >
            <img className="filter-chip-icon" src="/icons/icon_streetlight.png" alt="" />
            Streetlight
          </button>
          <button
            className={`filter-chip ${selectedLevels.includes('daylight') ? 'active' : ''}`}
            onClick={() => toggleFilter(selectedLevels, setSelectedLevels, 'daylight')}
          >
            <img className="filter-chip-icon" src="/icons/icon_daylight.png" alt="" />
            Daylight
          </button>
          <button
            className={`filter-chip ${selectedLevels.includes('sunshield') ? 'active' : ''}`}
            onClick={() => toggleFilter(selectedLevels, setSelectedLevels, 'sunshield')}
          >
            <img className="filter-chip-icon" src="/icons/icon_sunshield.png" alt="" />
            Sunshield
          </button>
        </div>

        <div className="filter-meta">
          {filtersActive ? (
            <button className="filter-clear" onClick={clearFilters}>
              {t('progress.clearFilters')}
            </button>
          ) : (
            <span></span>
          )}
          <span className="filter-count">{t('progress.activitiesFound', { count: filteredActivities.length })}</span>
        </div>
      </div>

      {loading && <p>{t('common.loading')}</p>}
      {error && <p>{error}</p>}
      {!loading && !error && activities.length > 0 && (
        <>
          {(searchActive || filtersActive) && filteredActivities.length === 0 ? (
            <div className="filter-empty-state">
              {searchActive ? t('progress.noMatchSearch') : t('progress.noMatchFilters')}
            </div>
          ) : (
            <ActivityList
              activities={filteredActivities}
              onLoadMore={loadMoreActivities}
              hasMore={hasMore}
              isLoading={loading}
              onEdit={handleEdit}
              onDelete={handleDeleteRequest}
            />
          )}
        </>
      )}
      {!loading && !error && activities.length === 0 && (
        <p>{error ? t('progress.noActivitiesError') : t('progress.noActivities')}</p>
      )}
      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontFamily: 'Poppins, sans-serif', fontSize: '16px', color: '#000033', marginBottom: '8px' }}>
              {t('progress.deleteConfirmTitle')}
            </h3>
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#808080', marginBottom: '20px', lineHeight: '1.5' }}>
              {t('progress.deleteConfirmMessage')}
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDeleteConfirm(null)}
                style={{
                  padding: '8px 20px',
                  border: '1px solid #E0E0E0',
                  borderRadius: '6px',
                  background: '#fff',
                  color: '#808080',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleDeleteConfirm}
                style={{
                  padding: '8px 20px',
                  border: 'none',
                  borderRadius: '6px',
                  background: '#D32F2F',
                  color: '#fff',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {t('progress.deleteConfirmButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      <FeedbackLink />
      <BottomNav />
    </div>
  );
};

export default ProgressScreen;
